import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  FormControl,
  FormHelperText,
  Paper,
  Divider,
  Input,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import CancelIcon from '@mui/icons-material/Cancel'; // For removing selected file
import DescriptionIcon from '@mui/icons-material/Description'; // For documents
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Style for the modal box
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '92%', sm: '55%', md: '45%' }, // responsive and elegant
  maxHeight: '90vh', // prevents overflow beyond viewport
  overflowY: 'auto', // adds scroll if content too tall
  bgcolor: 'background.paper',
  borderRadius: 3,
  boxShadow: 24, // stronger elevation for a premium feel
  p: { xs: 2.5, sm: 4 },
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  border: '1px solid',
  borderColor: 'divider',
  // Smooth animation for modal entrance
  transition: 'all 0.3s ease-in-out',

  // Nice scrollbar styling for overflow content
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0,0,0,0.2)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: 'rgba(0,0,0,0.3)',
  },
};


function PostModal({ open, handleClose, onSubmit, id, editingPost = null }) {
  const [content, setContent] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileType, setFileType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with editing post data
  useEffect(() => {
    if (editingPost) {
      setContent(editingPost.content || '');
      if (editingPost.mediaFile) {
        setFileName(editingPost.mediaFileName || 'Current media file');
        setFileType(editingPost.mediaFile.startsWith('data:image') ? 'image' : 'video');
      }
    }
  }, [editingPost]);

  const getFileType = (file) => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    return 'document';
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setMediaFile(file);
      setFileName(file.name);
      setFileType(getFileType(file));
    } else {
      setMediaFile(null);
      setFileName('');
      setFileType('');
    }
  };

  const handleRemoveFile = () => {
    setMediaFile(null);
    setFileName('');
    setFileType('');
    // Reset file input value if needed, though setting to null usually suffices
    const fileInput = document.getElementById('media-file-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!content && !mediaFile) {
      alert('Please enter content or select a media file to post.');
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('Content', content);
      
      if (mediaFile) {
        formData.append('MediaFile', mediaFile);
      }
      
      if (editingPost) {
        formData.append('ModifiedBy', sessionStorage.getItem('userId'));
      } else {
        formData.append('EmployeeId', sessionStorage.getItem('userId'));
        formData.append('groupId', id);
      }

      await onSubmit(formData, editingPost?.id);
      
      // Reset form only after successful submission
      setContent('');
      setMediaFile(null);
      setFileName('');
      setFileType('');
      
      // Close modal only after successful submission
      handleClose();
    } catch (error) {
      console.error('Error submitting post:', error);
      // Don't close modal on error
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    if (!isSubmitting) {
      handleClose();
    }
  };

  const handleRichTextChange = (value) => {
    setContent(value);
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="create-post-modal-title"
      aria-describedby="create-post-modal-description"
    >
      <Box sx={style} component="form" onSubmit={handleSubmit}>
        {/* Modal Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography id="create-post-modal-title" variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            {editingPost ? 'Edit Post' : 'Create New Post'}
          </Typography>
          <IconButton onClick={handleModalClose} aria-label="close" disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 1 }} />

        {/* Rich Text Editor instead of TextField */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            What's on your mind?
          </Typography>
          <ReactQuill
            theme="snow"
            value={content}
            onChange={handleRichTextChange}
            placeholder="Share your thoughts, ask a question, or post an update..."
            style={{
              borderRadius: '12px',
              backgroundColor: '#fdfdfd',
              marginBottom: '20px'
            }}
          />
        </Box>

        {/* File Upload Section */}
        <FormControl fullWidth>
          <FormHelperText sx={{ mb: 1, ml: 0 }}>Attach Media (Optional)</FormHelperText>
          <Input
            id="media-file-upload"
            type="file"
            inputProps={{ accept: 'image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip' }} // Accept all file types
            onChange={handleFileChange}
            sx={{ display: 'none' }} // Hide native file input
          />
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              borderColor: 'divider',
              borderRadius: '12px', // Match textfield rounding
              backgroundColor: '#fdfdfd',
              transition: 'border-color 0.3s ease-in-out',
              '&:hover': {
                borderColor: 'primary.main',
              },
              minHeight: 60, // Ensure clickable area is decent size
            }}
            onClick={() => document.getElementById('media-file-upload').click()}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <CloudUploadIcon color="primary" sx={{ fontSize: 30 }} /> {/* Prominent upload icon */}
              <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                {fileName || 'Drag & Drop your file here or click to browse'}
              </Typography>
            </Stack>
            <Button size="small" variant="contained" component="span" sx={{ borderRadius: '8px' }}>
              Browse
            </Button>
          </Paper>
          {fileName && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, justifyContent: 'space-between', pr: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    {fileType === 'image' && <ImageIcon color="action" fontSize="small" />}
                    {fileType === 'video' && <VideoFileIcon color="action" fontSize="small" />}
                    {fileType === 'document' && <DescriptionIcon color="action" fontSize="small" />}
                    <Typography variant="caption" color="text.secondary">
                        {fileName}
                    </Typography>
                </Stack>
                <IconButton size="small" onClick={handleRemoveFile} aria-label="remove file">
                    <CancelIcon fontSize="small" color="error" />
                </IconButton>
            </Box>
          )}
        </FormControl>

        {/* Submit Button */}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          sx={{ 
            mt: 2, 
            py: 1.2, 
            borderRadius: '12px', 
            fontWeight: 600, 
            fontSize: '1rem',
            position: 'relative'
          }}
          disabled={(!content.trim() && !mediaFile) || isSubmitting}
        >
          {isSubmitting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} color="inherit" />
              {editingPost ? 'Updating Post...' : 'Publishing Post...'}
            </Box>
          ) : (
            editingPost ? 'Update Post' : 'Publish Post'
          )}
        </Button>

        {/* Loading overlay */}
        {isSubmitting && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              pointerEvents: 'none'
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={40} />
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                {editingPost ? 'Updating your post...' : 'Creating your post...'}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
}

export default PostModal;